import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import MediaListContainer from './MediaListContainer'
import PortalModal from 'components/Shared/PortalModal'
import MediaGroupPreviewModal from './element/PreviewModal/MediaGroupPreviewModal'
import { buildLiveGroupListItem, buildMediaListItems } from '../../buildMediaListItems'
import { useTranslation } from 'react-i18next'
import MediaListCard from './element/MediaListCard/MediaListCard'
import ErrorMessage from 'components/Shared/ErrorMessage'

export default function MediaListV2({
  media,
  selectiveMode,
  selectedSubGroupId = null,
  setSelectedSubGroupId,
  isCoCreateReady = false,
  readonly = false,
  // onRemoveOriginal,
  onRemoveSubGroup,
  // onReplaceOriginal,
  onAddToCoCreateSlot,
  onAddLibraryToCoCreateSlot,
  onReplaceLibraryToCoCreateSlot,
  onReplaceInCoCreateSlot,
  onRemoveFromCoCreateSlot,
  isCampaignRunning = false,
  onViewGeneratedVariants,
}) {
  const [expandedPreview, setExpandedPreview] = useState(null)
  const [slotError, setSlotError] = useState(null)
  const { t } = useTranslation()

  const listItems = useMemo(() => buildMediaListItems(media), [media])

  const isGroupPreview = Boolean(expandedPreview?.subGroupId)
  const liveGroup = useMemo(() => {
    if (!isGroupPreview) {
      return null
    }

    return buildLiveGroupListItem(media, expandedPreview)
  }, [isGroupPreview, media, expandedPreview])

  useEffect(() => {
    if (isGroupPreview && expandedPreview && !liveGroup) {
      setExpandedPreview(null)
    }
  }, [isGroupPreview, expandedPreview, liveGroup])

  const modalTitle = t('Recommendations.campaign.setup.media.previewVariants')

  const originalMediaForGroup = useMemo(() => {
    if (!expandedPreview?.originalId) {
      return null
    }

    return media.find((item) => item.id === expandedPreview.originalId) ?? null
  }, [expandedPreview?.originalId, media])

  if (!listItems.length) {
    return null
  }

  const canRemove = !readonly && !isCampaignRunning

  return (
    <>
      {slotError && (
        <ErrorMessage style={{ justifyContent: 'start' }} variant="light" classNames="mt-0">{slotError}</ErrorMessage>
      )}

      <MediaListContainer>
        {listItems.map((listItem) => {
          const anchor = listItem.anchor
          if (!anchor) {
            return null
          }

          const isVideoOriginal = anchor?.type?.startsWith('video')
          const groupHasHeadline = listItem.images.some((image) => image.headline != null)
          const isSelected = String(selectedSubGroupId) === String(listItem.subGroupId)
          const disableSelection = selectiveMode && (isVideoOriginal || groupHasHeadline)

          const handleRemoveSubGroup = canRemove
            ? () => {
              setSlotError(null)
              onRemoveSubGroup?.(listItem.subGroupId)
            }
            : undefined
          const handleRemoveFailedMedia = canRemove
            ? () => {
              setSlotError(null)
              onRemoveFromCoCreateSlot?.(anchor.id)
            }
            : undefined

          const originalMediaForCard = listItem.originalId
            ? media.find((item) => item.id === listItem.originalId) ?? null
            : null

          return (
            <MediaListCard
              key={listItem.key}
              item={anchor}
              selectiveMode={selectiveMode}
              disableSelection={disableSelection}
              isSelected={isSelected}
              onSelect={() => setSelectedSubGroupId?.(listItem.subGroupId)}
              isReady={isCoCreateReady && Boolean(anchor.generating)}
              showLabel={true}
              mediaLabel={{
                variant: isVideoOriginal ? 'video' : (anchor.is_generated || groupHasHeadline) ? 'generated' : 'uploaded',
                headline: listItem.headline,
              }}
              subGroupItems={listItem.images}
              subGroupId={listItem.subGroupId}
              originalId={listItem.originalId}
              originalMedia={originalMediaForCard}
              isCampaignRunning={isCampaignRunning}
              readonly={readonly}
              onRemove={handleRemoveFailedMedia}
              onRemoveSubGroup={handleRemoveSubGroup}
              onPreviewClick={() => setExpandedPreview(listItem)}
              onViewVariants={onViewGeneratedVariants}
              onAddToSlot={onAddToCoCreateSlot}
              onAddLibraryToSlot={onAddLibraryToCoCreateSlot}
              onReplaceLibraryToSlot={onReplaceLibraryToCoCreateSlot}
              onReplaceInSlot={onReplaceInCoCreateSlot}
              onRemoveFromSlot={onRemoveFromCoCreateSlot}
              setSlotError={setSlotError}
            />
          )
        })}
      </MediaListContainer>

      <PortalModal
        isOpen={!!expandedPreview}
        className='full-height media-group-preview-portal-modal'
        onClickOutside={() => setExpandedPreview(null)}
        title={modalTitle}
      >
        {isGroupPreview && liveGroup ? (
          <MediaGroupPreviewModal
            group={liveGroup}
            originalMedia={originalMediaForGroup}
            isCampaignRunning={isCampaignRunning}
            readonly={readonly}
            onAddToSlot={onAddToCoCreateSlot}
            onAddLibraryToSlot={onAddLibraryToCoCreateSlot}
            onReplaceLibraryToSlot={onReplaceLibraryToCoCreateSlot}
            onReplaceInSlot={onReplaceInCoCreateSlot}
            onRemoveFromSlot={onRemoveFromCoCreateSlot}
          />
        ) : null}
      </PortalModal>
    </>
  )
}

MediaListV2.propTypes = {
  media: PropTypes.array,
  selectiveMode: PropTypes.bool,
  selectedSubGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedSubGroupId: PropTypes.func,
  isCoCreateReady: PropTypes.bool,
  readonly: PropTypes.bool,
  isCampaignRunning: PropTypes.bool,
  onRemoveOriginal: PropTypes.func,
  onRemoveSubGroup: PropTypes.func,
  onAddToCoCreateSlot: PropTypes.func,
  onAddLibraryToCoCreateSlot: PropTypes.func,
  onReplaceLibraryToCoCreateSlot: PropTypes.func,
  onReplaceInCoCreateSlot: PropTypes.func,
  onRemoveFromCoCreateSlot: PropTypes.func,
  onViewGeneratedVariants: PropTypes.func,
}
